import { useSearchParams } from "react-router-dom";
import { Formik } from "formik";
import type { Tenant } from "@repo/types";
import * as yup from "yup";
import { useTenantStore } from "@/stores/tenantStore";
import { useEffect } from "react";
import { CreateOrgStep } from "../setupSteps/CreateOrgStep";
import { CheckOutStep } from "../setupSteps/CheckoutStep";
import { VerifySubscriptionStep } from "../setupSteps/VerifySubscriptionStep";
import { CreateTeamsStep } from "../setupSteps/CreateTeamsStep";
import { SelectSubscriptionStep } from "../setupSteps/SelectSubscription";
import { CompleteStep } from "../setupSteps/CompleteStep";
import type { OnlyRequire } from "@repo/lib/types";
import { tenantRepo } from "@/repositories/tenantRepo";
import { take } from "rxjs";

type SetupOrganizationPageProps = {
  stepId: string;
};

export const SetupOrganizationPage = ({
  stepId,
}: SetupOrganizationPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { setTenant, tenant: tenantData } = useTenantStore();

  useEffect(() => {
    const tenantId = searchParams.get("tenantId");
    if (tenantId) {
      tenantRepo
        .get(tenantId)
        .pipe(take(1))
        .subscribe((tenant) => setTenant(tenant));
    }
  }, [searchParams, setTenant]);

  const STEPS = [
    // Step 1
    {
      tenantIdRequired: false,
      component: (key: string) => {
        return (
          <Formik
            key={key}
            initialValues={
              { name: "", friendlyId: "" } as OnlyRequire<
                Tenant,
                "name" | "friendlyId"
              >
            }
            onSubmit={async (values) => {
              const newTenant = await tenantRepo.create(values);
              setTenant(newTenant);
              setSearchParams({ stepId: "2", tenantId: newTenant.id });
            }}
            validationSchema={yup.object({
              name: yup.string().required("Organization name is required"),
              friendlyId: yup
                .string()
                .matches(
                  /^[a-z0-9]+$/,
                  "Invalid id. Only lowercase alphanumeric characters are allowed."
                )
                .required("Unique ID is required"),
            })}
          >
            {({ handleSubmit, isSubmitting }) => (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <CreateOrgStep isSubmitting={isSubmitting} />
              </form>
            )}
          </Formik>
        );
      },
    },
    // Step 2
    {
      tenantIdRequired: true,
      component: (key: string) => {
        if (!tenantData?.id) return null;
        return (
          <div key={key} className="flex flex-col gap-4">
            <SelectSubscriptionStep
              organizationMunicipalityId={
                tenantData?.organizationMunicipalityId
              }
              organizationType={tenantData?.organizationMunicipalityType}
              tenantId={tenantData?.id}
            />
          </div>
        );
      },
    },
    // Step 3
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <CheckOutStep />
          </div>
        );
      },
    },
    // Step 4
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            <VerifySubscriptionStep />
          </div>
        );
      },
    },
    // Step 5
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            {tenantData?.id && <CreateTeamsStep tenantId={tenantData?.id} />}
          </div>
        );
      },
    },
    // Step 6 / Redirect
    {
      tenantIdRequired: true,
      component: (key: string) => {
        return (
          <div key={key} className="flex flex-col gap-4">
            {tenantData?.id && <CompleteStep />}
          </div>
        );
      },
    },
  ];

  return (
    <div className="mx-auto mt-12 max-w-screen-lg w-full bg-form p-4 rounded-lg relative">
      {STEPS.map((step, index) => {
        if (index + 1 === parseInt(stepId)) {
          if (step.tenantIdRequired) {
            if (tenantData) {
              return step.component(`step-${index}`);
            }
            return null;
          }

          return step.component(`step-${index}`);
        }
        return null;
      })}
    </div>
  );
};
